import * as i0 from '@angular/core';
import { EventEmitter, ChangeDetectorRef, inject, Component, ChangeDetectionStrategy, Input, HostBinding, Output, ContentChildren } from '@angular/core';
import { merge } from 'rxjs';
import { startWith, switchMap, filter } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseControlComponent, ControlHintComponent } from '@knorr-bremse-portals/ngx-components/internal/controls';

class RadioOptionComponent {
    constructor() {
        this._required = false;
        this._disabled = false;
        this._checked = false;
        /** Event emitted when the option is checked or unchecked. */
        this.checkedChange = new EventEmitter();
        /** Event emitted when the option was touched. */
        this.touchedChange = new EventEmitter();
        this.cdr = inject(ChangeDetectorRef);
    }
    get name() { return this._name; }
    set name(value) {
        this._name = value;
        this.cdr.detectChanges(); // this is probably not good performance wise, but markForCheck does not work properly
    }
    /** Whether the component is required. */
    get required() { return this._required; }
    set required(value) { this._required = coerceBooleanProperty(value); }
    /** Whether the component is disabled. */
    get disabled() { return this._disabled; }
    set disabled(value) { this._disabled = coerceBooleanProperty(value); }
    /** Whether or not the option is currently checked. */
    get checked() { return this._checked; }
    set checked(value) {
        this._checked = coerceBooleanProperty(value);
        this.cdr.markForCheck();
    }
    onInputChange(event) {
        this.checked = event.target.checked;
        this.checkedChange.next(this);
    }
    onTouched() {
        this.touchedChange.next(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: RadioOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: RadioOptionComponent, isStandalone: true, selector: "kb-radio-option", inputs: { name: "name", value: "value", tabIndex: "tabIndex", required: "required", disabled: "disabled", checked: "checked" }, outputs: { checkedChange: "checkedChange", touchedChange: "touchedChange" }, host: { properties: { "class.disabled": "this.disabled" } }, ngImport: i0, template: "<label>\n  <input type=\"radio\" [value]=\"value\" [name]=\"name\" [tabIndex]=\"tabIndex\" [required]=\"required\" [disabled]=\"disabled\" [checked]=\"checked\" (blur)=\"onTouched()\" (change)=\"onInputChange($event)\">\n  <ng-content></ng-content>\n</label>\n", styles: [":host{display:inline-block}:host.disabled label{cursor:not-allowed}:host label{font-size:var(--kb-typography--size-normal);line-height:var(--kb-typography--height-normal);cursor:pointer}:host label input{outline-offset:calc(var(--kb-width--outline) * -1);outline-width:var(--kb-width--outline);appearance:none;margin:0;height:var(--kb-typography--size-normal);width:var(--kb-typography--size-normal);background-color:var(--kb-color--background);border-radius:50%;margin-right:var(--kb-gap--inner-row--text);cursor:pointer;transform:translateY(calc(var(--kb-typography--size-normal) / 10))}:host label input:focus{outline-style:solid;outline-color:var(--kb-color--on-secondary--low)}:host label input[disabled]{cursor:not-allowed;background-color:var(--kb-color--secondary--surface-disabled);color:var(--kb-color--on-secondary--surface-disabled)}:host label input:before{content:\"\";display:block;width:calc(var(--kb-typography--size-normal) - var(--kb-width--outline) * 2);height:calc(var(--kb-typography--size-normal) - var(--kb-width--outline) * 2);border-radius:50%;transform:scale(0);transition:transform var(--kb-speed--fast);background-color:var(--kb-color--on-background--high);margin:var(--kb-width--outline)}:host label input:checked:before{transform:scale(1)}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: RadioOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-radio-option', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [], template: "<label>\n  <input type=\"radio\" [value]=\"value\" [name]=\"name\" [tabIndex]=\"tabIndex\" [required]=\"required\" [disabled]=\"disabled\" [checked]=\"checked\" (blur)=\"onTouched()\" (change)=\"onInputChange($event)\">\n  <ng-content></ng-content>\n</label>\n", styles: [":host{display:inline-block}:host.disabled label{cursor:not-allowed}:host label{font-size:var(--kb-typography--size-normal);line-height:var(--kb-typography--height-normal);cursor:pointer}:host label input{outline-offset:calc(var(--kb-width--outline) * -1);outline-width:var(--kb-width--outline);appearance:none;margin:0;height:var(--kb-typography--size-normal);width:var(--kb-typography--size-normal);background-color:var(--kb-color--background);border-radius:50%;margin-right:var(--kb-gap--inner-row--text);cursor:pointer;transform:translateY(calc(var(--kb-typography--size-normal) / 10))}:host label input:focus{outline-style:solid;outline-color:var(--kb-color--on-secondary--low)}:host label input[disabled]{cursor:not-allowed;background-color:var(--kb-color--secondary--surface-disabled);color:var(--kb-color--on-secondary--surface-disabled)}:host label input:before{content:\"\";display:block;width:calc(var(--kb-typography--size-normal) - var(--kb-width--outline) * 2);height:calc(var(--kb-typography--size-normal) - var(--kb-width--outline) * 2);border-radius:50%;transform:scale(0);transition:transform var(--kb-speed--fast);background-color:var(--kb-color--on-background--high);margin:var(--kb-width--outline)}:host label input:checked:before{transform:scale(1)}\n"] }]
        }], propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }, {
                type: Input
            }], checked: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }], touchedChange: [{
                type: Output
            }] } });

class RadioComponent extends BaseControlComponent {
    constructor() {
        super(...arguments);
        this.showRequired = true;
        this.valueChange = new EventEmitter();
        /** `View -> model callback called when value changes` */
        this.onChange = () => undefined;
        /** `View -> model callback called when select has been touched` */
        this.onTouched = () => undefined;
        this.afterContentInit = false;
        this.valueBeforeContentInit = this.value;
    }
    get value() { return this._value; }
    set value(newValue) {
        this._value = newValue;
        this.selectOption(this._value);
    }
    ngAfterContentInit() {
        this.afterContentInit = true;
        if (this.valueBeforeContentInit !== this.value) {
            this.value = this.valueBeforeContentInit;
        }
        this.setupOptionsChangesHandler();
        this.setupOptionsCheckedChangeHandler();
        this.setupOptionsTouchedChangeHandler();
    }
    selectOption(value) {
        const option = this.options.find(option => option.value === value);
        this.options.forEach(option => option.checked = false);
        if (option) {
            option.checked = true;
        }
    }
    setupOptionsChangesHandler() {
        this.options.changes.pipe(startWith(this.options), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.updateOptionsName();
        });
    }
    nameUpdated() {
        this.updateOptionsName();
    }
    updateOptionsName() {
        if (!this.options) {
            return;
        }
        this.options.forEach((option) => {
            option.name = this.name;
        });
    }
    setupOptionsCheckedChangeHandler() {
        this.options.changes.pipe(startWith(this.options), switchMap(() => merge(...this.options.map(option => option.checkedChange))), filter((option) => option.checked === true), takeUntilDestroyed(this.destroyRef)).subscribe((selectedOption) => {
            this._value = selectedOption.value;
            this.valueChange.emit(this.value);
            this.onChange(this.value);
            this.ref.markForCheck(); // update super.showError
        });
    }
    setupOptionsTouchedChangeHandler() {
        this.options.changes.pipe(startWith(this.options), switchMap(() => merge(...this.options.map(option => option.touchedChange))), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.onTouched();
            this.ref.markForCheck(); // update super.showError
        });
    }
    writeValue(value) {
        if (!this.afterContentInit) {
            this.valueBeforeContentInit = value;
        }
        else {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: RadioComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: RadioComponent, isStandalone: true, selector: "kb-radio", inputs: { showRequired: "showRequired", value: "value" }, outputs: { valueChange: "valueChange" }, queries: [{ propertyName: "options", predicate: RadioOptionComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<fieldset [attr.aria-describedby]=\"hintElement ? hintElement.id : null\">\n  <div class=\"label-line\">\n    <legend>\n      <ng-content select=\"kb-label\"></ng-content> {{required && showRequired ? ' *' : ''}}\n    </legend>\n    <kb-control-hint #hintElement [showError]=\"showError\">\n      <ng-content select=\"kb-hint\" ngProjectAs=\"kb-hint\"></ng-content>\n      <ng-content select=\"kb-error\" ngProjectAs=\"kb-error\"></ng-content>\n    </kb-control-hint>\n  </div>\n  <div class=\"options\">\n    <ng-content></ng-content>\n  </div>\n</fieldset>\n", styles: [":host{display:inline-block}:host.ng-invalid.ng-touched,:host.ng-invalid.kb-submitted{color:var(--kb-color--on-background--error)}:host fieldset{border:0;padding:0;margin:0}:host fieldset .options{display:flex;flex-wrap:wrap;gap:var(--kb-gap--outer-column--related) var(--kb-gap--outer-row--related)}:host fieldset .options radio-option{flex-shrink:0}:host fieldset .label-line{display:flex;align-items:baseline;gap:var(--kb-gap--inner-row);padding-bottom:var(--kb-gap--outer-column--related);padding-left:0 .125rem;padding-right:0 .125rem}:host fieldset .label-line legend{font-size:var(--kb-typography--size-normal);line-height:var(--kb-typography--height-normal)}:host fieldset .label-line kb-control-hint{flex-shrink:0}\n"], dependencies: [{ kind: "component", type: ControlHintComponent, selector: "kb-control-hint", inputs: ["showError"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: RadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-radio', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [ControlHintComponent], template: "<fieldset [attr.aria-describedby]=\"hintElement ? hintElement.id : null\">\n  <div class=\"label-line\">\n    <legend>\n      <ng-content select=\"kb-label\"></ng-content> {{required && showRequired ? ' *' : ''}}\n    </legend>\n    <kb-control-hint #hintElement [showError]=\"showError\">\n      <ng-content select=\"kb-hint\" ngProjectAs=\"kb-hint\"></ng-content>\n      <ng-content select=\"kb-error\" ngProjectAs=\"kb-error\"></ng-content>\n    </kb-control-hint>\n  </div>\n  <div class=\"options\">\n    <ng-content></ng-content>\n  </div>\n</fieldset>\n", styles: [":host{display:inline-block}:host.ng-invalid.ng-touched,:host.ng-invalid.kb-submitted{color:var(--kb-color--on-background--error)}:host fieldset{border:0;padding:0;margin:0}:host fieldset .options{display:flex;flex-wrap:wrap;gap:var(--kb-gap--outer-column--related) var(--kb-gap--outer-row--related)}:host fieldset .options radio-option{flex-shrink:0}:host fieldset .label-line{display:flex;align-items:baseline;gap:var(--kb-gap--inner-row);padding-bottom:var(--kb-gap--outer-column--related);padding-left:0 .125rem;padding-right:0 .125rem}:host fieldset .label-line legend{font-size:var(--kb-typography--size-normal);line-height:var(--kb-typography--height-normal)}:host fieldset .label-line kb-control-hint{flex-shrink:0}\n"] }]
        }], propDecorators: { showRequired: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], options: [{
                type: ContentChildren,
                args: [RadioOptionComponent, { descendants: true }]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { RadioComponent, RadioOptionComponent };
